<template>
  <div
    class="main-product critical-component-clear"
    :class="{ [$class('loading')]: state === 'loading' }"
  >
    <div class="grid container main-product__block">
      <div
        id="product-gallery"
        class="col xs-span l6"
        data-sign-post-location="main"
        js-sign-post
      >
        <div class="main-product__gallery-scroll">
          <product-gallery
            class="main-product__gallery"
            :active-code="activeSwatch.code"
            :current-index="currentIndex"
            :current-namespace="currentNamespace"
            :is-gift-card="product.giftCard"
            layout="tabs-grid"
            :sku="variantFromSwatch?.sku || null"
            :loading="state === 'loading'"
            :product="product || liquid"
            @update-current-index="handleUpdateCurrentIndex"
            @update-current-namespace="handleUpdateCurrentNamespace"
            @update-featured-image="handleUpdateFeaturedImage"
          />
        </div>
      </div>
      <!-- Product Details -->
      <div
        class="col xs-span l7-11 main-product__details-col"
        :class="{ 'main-product__details-col--locked': isDetailsLocked }"
      >
        <div
          ref="aboveFold"
          class="main-product__above-fold"
        >
          <button
            v-if="previousPlpUrl"
            class="main-product__click-back-link"
            type="button"
            @click="handleclickback"
          >
            <icon-chevron-left class="main-product__click-back-icon" />
            <span v-if="section.clickBackShowCollectionName">
              {{ getPreviousCollectionName() }}
            </span>
            <span v-else>
              {{ section.clickback }}
            </span>
          </button>
          <div class="main-product__details">
            <h1
              id="template-title"
              class="
                main-product__title
                text-heading-l
              "
              v-text="product.title || liquid.title"
            />

            <product-prices
              class="main-product__prices-container"
              :available="variant.available"
              :compare-at-price="variant.compareAtPrice?.amount ||
                variantFromSwatch?.compareAtPrice?.amount"
              :is-surplus="isSurplus"
              :render-final-sale-copy="isFinalSale"
              :loading="state === 'loading'"
              :price="variant.price?.amount ||
                variantFromSwatch?.price?.amount"
              :varies="variant.price?.varies ||
                variantFromSwatch?.price?.varies"
              :gift-card-price-range="giftCardPriceRange"
              :show-btn-discount="showBtnDiscount"
            />
            <p
              v-if="product.tagline || liquid.tagline"
              class="main-product__tagline text-body-m-regular"
              v-text="product.tagline || liquid.tagline"
            />

            <div
              v-else-if="giftCardDescription"
              class="main-product__tagline text-body-m-regular"
              v-html="giftCardDescription"
            />

            <product-form
              v-if="product || liquid"
              class="main-product__form"
              :color-options="colorOptions"
              :filtered-media-image="filteredMediaImage"
              :has-size-guide="Boolean(sizeGuideData)"
              :is-one-size="isOneSize"
              :preorder-date="preorderDate"
              :is-size-guide-expanded="activeDrawers.sizeGuideDrawer"
              :loading="state === 'loading'"
              :loyalty-copy-info="section.loyalty.copy"
              namespace="default"
              :out-of-stock-expanded="activeDrawers.notifyMeDrawer"
              :product="product || liquid"
              :section="section"
              :pdp-one-title="pdpOneTitle"
              :pdp-one-url="pdpOneUrl"
              :pdp-two-title="pdpTwoTitle"
              :pdp-two-url="pdpTwoUrl"
              :variant="variant || variantFromSwatch"
              :show-btn-discount="showBtnDiscount"
              @open-size-guide="handleOpenSizeGuide"
              @open-notify-me-drawer="handleOpenNotifyMeDrawer"
              @update-active-swatch="handleActiveSwatchUpdate"
              @update-variant="handleVariantUpdate"
            />
          </div>

          <div
            v-if="!product.giftCard || !liquid.giftCard"
            class="main-product__below-fold-sticky"
          >
            <div class="main-product__accordion-container">
              <accordion-snippet
                :accordion-data="accordionData"
                :expanded-index="0"
                block-class="main-product"
                class="text-body-m-regular"
                use-minus-icon
              />

              <btn
                v-if="isDenim"
                class="main-product__trigger"
                class-label="text-body-m-regular"
                :label="$string('product.accordion.denim_measurements_title')"
                modifiers="block"
                show-icon
                type="button"
                @click.prevent="handleOpenSizeGuide"
              >
                <template #icon>
                  <icon-chevron-right />
                </template>
              </btn>

              <btn
                v-if="hasStoreAvailabilityDrawer"
                class="main-product__trigger"
                class-label="text-body-m-regular"
                :label="$string('product.accordion.store_stock_title')"
                modifiers="block"
                show-icon
                type="button"
                @click.prevent="openStoreAvailabilityDrawer"
              >
                <template #icon>
                  <icon-chevron-right />
                </template>
              </btn>
            </div>

            <template v-if="section.loyalty.title || section.loyalty.copy">
              <a
                v-if="Boolean(section.loyalty.url)"
                class="main-product__loyalty"
                :href="section.loyalty.url"
              >
                <h2
                  class="text-other-label-regular"
                  v-text="section.loyalty.title"
                />
                <div class="main-product__loyalty__content">
                  <div class="main-product__loyalty__buttons">
                    <p
                      class="main-product__loyalty__text"
                    >
                      <span class="loyalty__para">
                        {{ section.loyalty.copy }}
                      </span>

                      <btn
                        v-if="pdpOneTitle"
                        :aria-controls="ariaControls"
                        type="button"
                        :label="pdpOneTitle"
                        :url="pdpOneUrl"
                        :aria-expanded="showInfo"
                        modifiers="newunderlined"
                      />
                      <span
                        v-if="pdpOneTitle && pdpTwoTitle"
                        class="loyalty-points-messaging__bar"
                      >
                        |
                      </span>
                      <btn
                        v-if="pdpTwoTitle"
                        :aria-controls="ariaControls"
                        type="button"
                        :label="pdpTwoTitle"
                        :url="pdpTwoUrl"
                        :aria-expanded="showInfo"
                        modifiers="newunderlined"
                      />
                    </p>
                  </div>
                </div>
              </a>
              <div
                v-else
                class="main-product__loyalty"
              >
                <h2
                  class="text-other-label-regular"
                  v-text="section.loyalty.title"
                />
                <div class="main-product__loyalty__content">
                  <div class="main-product__loyalty__buttons">
                    <p
                      class="main-product__loyalty__text mb-m"
                    >
                      <span class="loyalty__para">
                        {{ section.loyalty.copy }}
                      </span>
                      <btn
                        v-if="pdpOneTitle"
                        :aria-controls="ariaControls"
                        type="button"
                        :label="pdpOneTitle"
                        :url="pdpOneUrl"
                        :aria-expanded="showInfo"
                        modifiers="newunderlined"
                      />
                      <span
                        v-if="pdpOneTitle && pdpTwoTitle"
                        class="loyalty-points-messaging__bar"
                      >
                        |
                      </span>
                      <btn
                        v-if="pdpTwoTitle"
                        :aria-controls="ariaControls"
                        type="button"
                        :label="pdpTwoTitle"
                        :url="pdpTwoUrl"
                        :aria-expanded="showInfo"
                        modifiers="newunderlined"
                      />
                    </p>
                  </div>
                </div>
              </div>
            </template>
            <div
              v-html="searchSpringRecScript"
            />
          </div>
        </div>
      </div>
    </div>
    <teleport to="body">
      <size-guide-drawer
        :help-content="section.sizeGuideHelp"
        :is-custom="Boolean(product.customSizeGuideTable) || Boolean(liquid.customSizeGuideTable)"
        :is-denim="isDenim"
        :size-guide-data="sizeGuideData"
        @update-expanded="handleActiveDrawerUpdate('sizeGuideDrawer', $event)"
      />
    </teleport>

    <div
      v-if="!product.giftCard || !liquid.giftCard"
      class="main-product__sticky grid"
      :class="{ 'main-product__sticky--is-visible': showStickyForm }"
    >
      <!-- Mobile sticky bar with pricing -->
      <div
        v-if="isMobile"
        class="main-product__sticky-mobile"
      >
        <div class="main-product__sticky-info">
          <h2
            class="main-product__sticky-title"
            v-text="product.title || liquid.title"
          />
        </div>

        <div class="main-product__sticky-actions">
          <btn
            aria-controls="product-form-drawer"
            :aria-expanded="activeDrawers.productFormDrawer"
            class="main-product__sticky-button"
            class-label="text-body-m-regular"
            :label="isOneSize ? addToCartText : $string('product.cta.add_to_cart')"
            modifiers="add-to-cart, block"
            type="button"
            :product="{
              compareAtPrice: {
                amount: variant.compareAtPrice?.amount || variantFromSwatch?.compareAtPrice?.amount
              },
              price: {
                amount: variant.price?.amount || variantFromSwatch?.price?.amount
              }
            }"
            :show-btn-discount="showBtnDiscount"
            @click.prevent="isOneSize ? handleAddToCart() : handleOpenFormDrawer()"
          />
        </div>
      </div>

      <!-- Desktop sticky content -->
      <div
        v-if="!isMobile"
        class="main-product__details col xs4 l2"
      >
        <h2
          class="main-product__title text-heading-l"
          v-text="product.title || liquid.title"
        />
        <product-prices
          class="main-product__prices-container"
          :available="variant.available"
          :compare-at-price="variant.compareAtPrice?.amount ||
            variantFromSwatch?.compareAtPrice?.amount"
          :loading="state === 'loading'"
          :price="variant.price?.amount ||
            variantFromSwatch?.price?.amount"
          :varies="variant.price?.varies ||
            variantFromSwatch?.price?.varies"
          :render-final-sale-copy="isFinalSale"
          :show-btn-discount="showBtnDiscount"
        />
      </div>

      <product-form
        v-if="renderStickyForm && !isMobile"
        class="main-product__form col xs4 l3-12"
        :filtered-media-image="filteredMediaImage"
        :is-one-size="isOneSize"
        :color-options="colorOptions"
        :preorder-date="preorderDate"
        :is-size-guide-drawer="activeDrawers.sizeGuideDrawer"
        is-sticky
        :loading="state === 'loading'"
        namespace="sticky"
        :out-of-stock-expanded="activeDrawers.notifyMeDrawer"
        :product="product || liquid"
        :section="section"
        :variant="variant"
        @open-size-guide="handleOpenSizeGuide"
        @open-notify-me-drawer="handleOpenNotifyMeDrawer"
        @update-active-swatch="handleActiveSwatchUpdate"
        @update-variant="handleVariantUpdate"
      />

      <span
        v-if="preorderDate && isMobile"
        class="product-form__preorder-message text-body-m-regular col xs4"
        v-text="$string('product.form.preorder_date_string', {
          replace: { date: preorderDate }
        })"
      />

      <teleport to="body">
        <overlay
          id="product-form-drawer"
          key="productFormDrawer"
          block-class="product-form-drawer"
          class-container="product-form-drawer__drawer"
          direction="up"
          namespace="productFormDrawer"
          show-overlay
          type="drawer"
          use-close-alt
        >
          <template #title>
            <span
              class="text-other-label-bold"
              v-text="productFormDrawerTitle"
            />
          </template>

          <template #body="overlay">
            <product-form-drawer
              :active-swatch="activeSwatch"
              :filtered-media-image="filteredMediaImage"
              :is-final-sale="isFinalSale"
              :is-size-guide-drawer="activeDrawers.sizeGuideDrawer"
              :preorder-date="preorderDate"
              :loading="state === 'loading'"
              namespace="sticky"
              :out-of-stock-expanded="activeDrawers.notifyMeDrawer"
              :overlay="overlay"
              :product="product || liquid"
              :section="section"
              :tag-colour="tagColour"
              :variant="variant"
              @open-size-guide="handleOpenSizeGuide"
              @open-notify-me-drawer="handleOpenNotifyMeDrawer"
              @update-confirmation-title="handleConfirmationTitleUpdate"
              @update-expanded="handleActiveDrawerUpdate('productFormDrawer', $event)"
              @update-variant="handleVariantUpdate"
            />
          </template>
        </overlay>
      </teleport>

      <teleport to="body">
        <notify-me-drawer
          :copy="section.notifyMeDrawer.copy"
          namespace="notify-me-drawer"
          :newsletter-data="section.notifyMeDrawer.newsletter"
          :product-id="product.id || liquid.id"
          :recommendations-id="section.notifyMeDrawer.recommendationsId"
          :success-copy="section.notifyMeDrawer.successCopy"
          :title="section.notifyMeDrawer.title"
          :variant-id="variant.id"
          @update-expanded="handleActiveDrawerUpdate('notifyMeDrawer', $event)"
        />
      </teleport>

      <teleport to="body">
        <store-availability-drawer
          :active-swatch="activeSwatch"
          :section="{
            enableDisplayNames: section.storeAvailabilityDrawer.enableDisplayNames,
            id: section.id,
          }"
          :product="product || liquid"
        />
      </teleport>
    </div>
  </div>
  <div
    ref="searchSpringContainer"
    class="container"
  />
</template>

<script>

/**
 * Vue: Main product (main-product)
 * -----------------------------------------------------------------------------
 * Main product page component.
 *
 * @param {Object} liquid - Formatted product Liquid object.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object} section - Section settings.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 *
 * @emits updateRequestObject - Emit new product object.
 *
 */
import { defineAsyncComponent } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import NotifyMeDrawer from '~async/notify-me-drawer/notify-me-drawer'
import ProductForm from '~async/product-form/product-form'
import ProductGallery from '~async/product-gallery/product-gallery'
import SizeGuideDrawer from '~async/size-guide-drawer/size-guide-drawer'
import StoreAvailabilityDrawer from '~async/store-availability-drawer/store-availability-drawer'
import AccordionSnippet from '~global/accordion-snippet/accordion-snippet'
import Btn from '~global/btn/btn'
import Overlay from '~global/overlay/overlay'
import ProductPrices from '~global/product-prices/product-prices'
import IconChevronRight from '~icons/directional-navigation/chevron/right.svg'
import IconChevronLeft from '~icons/directional-navigation/chevron/left-4.svg'
import { formatProduct } from '~/format/format-product'
import { setIntersectionObserver } from '~/helpers/general'
import { isColorCodeInStr } from '~/helpers/product'
import { emitUpdateRequestObject } from '~/helpers/vue'
import productQuery from '~graphql/product.gql'
import productsByTagQuery from '~graphql/productsByTag.gql'

export default {
  name: 'MainProduct',

  components: {
    AccordionSnippet,
    Btn,
    IconChevronRight,
    IconChevronLeft,
    NotifyMeDrawer,
    Overlay,
    ProductForm,
    ProductFormDrawer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.product-form-drawer' */
        '~async/product-form-drawer/product-form-drawer'
      ),
    }),
    ProductGallery,
    ProductPrices,
    SizeGuideDrawer,
    StoreAvailabilityDrawer,

  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        description: '',
        price: 0,
        title: '',
      }),
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    showBtnDiscount: {
      type: String,
      default: 'show',
    },
    pdpOneTitle: {
      type: String,
      default: '',
    },
    pdpOneUrl: {
      type: String,
      default: '',
    },
    pdpTwoTitle: {
      type: String,
      default: '',
    },
    pdpTwoUrl: {
      type: String,
      default: '',
    },
    section: {
      type: Object,
      default: () => ({
        autoOpenCartDrawer: true,
        displayQuantitySelector: true,
        hideGiftCardOptionNames: true,
        id: '',
        loyalty: {
          copy: '',
          title: '',
          url: '',
        },
        notifyMeDrawer: {
          copy: '',
          newsletter: {
            countries: [],
            customSource: '',
          },
          recommendationsId: '',
          successCopy: '',
          title: '',
        },
        ourShipping: {
          content: {
            body: '',
            title: '',
          },
          isEnabled: false,
        },
        tab2: {
          content: {
            body: '',
            title: '',
          },
          isEnabled: false,
        },
        tab3: {
          content: {
            body: '',
            title: '',
          },
          isEnabled: false,
        },
        tab4: {
          content: {
            body: '',
            title: '',
          },
          isEnabled: false,
        },
        tab5: {
          content: {
            body: '',
            title: '',
          },
          isEnabled: false,
        },
        ourCommitments: {
          content: {
            body: '',
            title: '',
          },
          isEnabled: false,
        },
        resetRecipientFormOnAdd: true,
        sizeGuideHelp: {
          copy: '',
          ctas: [],
          title: '',
        },
        altermessage: {
          message: [],
        },
        clickback: '',
      }),
    },
    variant: {
      type: [Boolean, Object],
      default: false,
    },
    tagColour: {
      type: String,
      default: '#000000',
    },
  },

  emits: {
    'update-request-object': emitUpdateRequestObject,
  },

  data() {
    return {
      accordionData: this.product.accordion || this.liquid.accordion,
      activeDrawers: {
        notifyMeDrawer: false,
        productFormDrawer: false,
        sizeGuideDrawer: false,
        storeAvailabilityDrawer: false,
      },
      activeSwatch: {},
      colorOptions: [],
      currentIndex: 0,
      currentNamespace: 'gallery',
      filteredMediaImage: this.product?.featuredImage,
      handle: this.$variable('page.handle'),
      productFormDrawerTitle: this.$string('product.form.select_size'),
      showStickyForm: false,
      state: 'loading',
      matchedProducts: [],
      useAddMoreText: false,
      variantFromSwatch: {},
      previousPlpUrl: null,
      isOriginalButtonVisible: true,
    }
  },
  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      if (this.preorderDate) {
        return this.$string('product.cta.preorder')
      }

      if (this.isMobile) {
        return this.$string('product.cta.select_size')
      }

      return this.useAddMoreText
        ? this.$string('product.cta.add_more_to_cart')
        : this.$string('product.cta.add_to_cart')
    },

    /**
     * Compute gift card description.
     * @returns {String|Boolean}
     */
    giftCardDescription() {
      return this.product.giftCard
        ? this.product.description.replace(
          /a[.]p[.]c[.]/gi,
          '<span class="site-name">A.P.C.</span>',
        )
        : false
    },

    /**
     * Compute gift card price range to render before variant selection.
     * @returns {Object|Boolean}
     */
    giftCardPriceRange() {
      return this.product.giftCard
        ? this.product.price
        : false
    },

    /**
     * Determine if store availability drawer component has been added to
     * product template.
     * @returns {Boolean}
     */
    hasStoreAvailabilityDrawer() {
      return this.getComponentExists('store-availability-drawer')
    },

    /**
     * Return if the product is type Denim.
     * @returns {Boolean}
     */
    isDenim() {
      const productType = this.product?.type
        ? this.product.type
        : this.liquid?.type

      if (!productType) {
        return productType
      }

      const productTypeSanitized = productType.toLowerCase()

      return productTypeSanitized.includes('denim') ||
        productTypeSanitized.includes('jeans')
    },

    /**
     * Compute if this is a final sale product.
     * @returns {Boolean}
     */
    isFinalSale() {
      const productData = this.product || this.liquid

      return productData.tags.some(tag => {
        return tag.toLowerCase().includes('final-sale')
      })
    },

    /**
     * Returns if the product is tagged as surplus product for surplus-related
     * messaging and styling.
     * @returns {Boolean}
     */
    isSurplus() {
      const productData = this.product || this.liquid

      return productData.tags.some((tag) => {
        return tag.toLowerCase().includes('surplus')
      })
    },

    /**
     * Compute if the product only has one size option.
     * Also returns true if size option doesn't exist (gift cards).
     * @returns {Boolean}
     */
    isOneSize() {
      const productOptions = this.product?.options || this.liquid?.options

      const sizeOption = productOptions.find(option => option.name === 'Size')
      if (!sizeOption) {
        return false
      }
      return sizeOption.values.length === 1
    },

    /**
     * Preorder date where applicable.
     * Returns false if no preorder tag.
     * @returns {String|Boolean}
     */
    preorderDate() {
      const productData = this.product || this.liquid

      const preorderTag = productData.tags.find(tag => {
        return tag.toLowerCase().includes('preorder')
      })

      return preorderTag ? preorderTag.split(':')[1] : false
    },

    /**
     * Compute product badge.
     * @returns {Boolean|String}
     */
    productBadge() {
      if (!this.product.tags) {
        return false
      }

      const badge = this.product.tags.find((tag) => tag.includes('badge:'))
      if (!badge) {
        return false
      }
      const parts = badge.replace('badge:', '').split(':')
      return {
        name: parts[0],
        color: parts[1] || '#000000',
      }
    },

    /**
     * Determine whether to render the sticky product form (i.e. add to cart
     * button).
     * @returns {Boolean}
     */
    renderStickyForm() {
      if (!this.isMobile) {
        return true
      }

      return this.isOneSize
    },

    /**
     * Return appropriate size guide data based on if source is product
     * metafields or collection metaobject.
     * @returns {Object|Array}
     */
    sizeGuideData() {
      return this.liquid.customSizeGuideTable
        ? this.liquid.customSizeGuideTable
        : this.liquid.sizeGuideTables
    },

    /**
     * Style group tag.
     * @returns {String|Boolean}
     */
    styleGroupTag() {
      if (this.product?.giftCard) {
        return false
      }

      const styleGroupTag = this.product?.tags?.find(tag => tag.includes('style-group:')) ||
        this.liquid?.tags?.find(tag => tag.includes('style-group:'))

      return styleGroupTag ? styleGroupTag : false
    },

    /**
     * Compute vendor collection URL.
     * @returns {String}
     */
    vendorCollectionUrl() {
      return this.product.vendor
        ? `${this.$variable('routes.normalisedRoot')}collections/vendors?q=${encodeURI(this.product.vendor)}`
        : ''
    },

    searchSpringRecScript() {
      // Use the product if it exists, otherwise fallback to liquid.
      const productData = this.product || this.liquid
      if (productData && productData.variants && productData.variants.length > 0) {
        const sku = productData.variants[0].sku
        const startingScript = '<script type="searchspring/personalized-recommendations" profile="bundle"> seed = "'
        // eslint-disable-next-line
        const endingScript = '";<\/script>'
        return startingScript + sku + endingScript
      }
      return ''
    },
  },

  watch: {

    /**
     * Watch active swatch to change pseudo selected variant.
     */
    activeSwatch() {
      this.setVariantFromSwatch()
    },

    /**
     * Watch handle for update to load new product.
     */
    handle() {
      this.queryProduct(this.handle)
    },

    /**
     * Watch variant to update the saved product in session storage.
     */
    variant() {
      this.saveToSessionStorage()
      this.setAccordionData()
    },

    /**
     * Watch matched products to update swatch info.
     */
    matchedProducts() {
      this.setColorOptions()
    },

    /**
     * Watch for product change.
     */
    product() {
      this.updateProductUrl(this.product.handle)

      if (!this.matchedProducts.length) {
        this.queryMatchedProducts()
      }

      if (!this.variant?.id || !this.isOneSize) {
        return
      }

      const activeVariantInProduct = this.product.variants?.find(variant => {
        return variant.id === this.variant.id
      })

      if (!activeVariantInProduct && this.activeSwatch) {
        const variantFromSwatch = this.getVariantFromSwatch()

        this.handleVariantUpdate(variantFromSwatch, true)
      }
    },
  },

  mounted() {
    this.setProduct()
    this.setAboveFoldIntersectionObserver()
    this.setAddToCartButtonObserver()
    this.setEventBusListeners()
    this.setVariantFromSwatch()
    this.saveToSessionStorage()
    this.setAccordionData()
    this.checkpreviousUrl()
    this.$nextTick().then(() => {
      this.initIntelliSuggest('you-may-also-like-pdp')
      this.initIntelliSuggest('customers-also-viewed')
      this.setIntelliSuggest()
    })
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Set cnvs event bus listeners.
     */
    setEventBusListeners() {
      cnvs.EventBus.listen('update-product', payload => {
        const newProduct = this.matchedProducts.find(product => {
          return product.handle === payload.handle
        })

        this.$emit('update-request-object', {
          response: newProduct,
          type: 'product',
        })

        this.handleActiveSwatchUpdate(payload)
      })

      cnvs.EventBus.listen('update-variant', variant => {
        if (this.variant === variant) {
          return
        }

        this.handleVariantUpdate(variant, false)
      })

      cnvs.EventBus.listen('store-availability-drawer-expanded', (payload) => {
        this.handleActiveDrawerUpdate('storeAvailabilityDrawer', payload)
      })
    },

    /**
     * Set Liquid product object to product.
     */
    setProduct(handle) {
      if (handle) {
        this.queryProduct(handle)
        return
      }

      if (!this.liquid.handle) {
        this.queryProduct()
        return
      }

      this.$emit('update-request-object', {
        response: this.liquid,
        type: 'product',
      })

      this.state = 'ready'
    },
    handleclickback() {
      window.history.back()
    },
    checkpreviousUrl() {
      const check = sessionStorage.getItem('cameFromPLP')
      if (check === 'true') {
        this.previousPlpUrl = sessionStorage.getItem('cameFromPLP')
      }

      sessionStorage.setItem('cameFromPLP', 'false')

      // Store the current PDP URL in sessionStorage

    },

    /**
     * Fetch the previous collection name from sessionStorage.
     */
    getPreviousCollectionName() {
      const collectionName = sessionStorage.getItem('plpCollectionName') || ''
      return collectionName
    },

    /**
     * Load products from GraphQL and sort currently active product to front.
     */
    async queryMatchedProducts() {
      if (!this.styleGroupTag) {
        this.setColorOptions()
        return
      }

      const query = `(tag:'${this.styleGroupTag}')`

      try {
        const response = await cnvs.Query({
          query: productsByTagQuery,
          variables: {
            country: this.$variable('store.country'),
            query,
            language: this.$variable('store.language'),
          },
        })

        if (!response.products.edges.length) {
          return
        }

        const tmpSwatches = []

        response.products.edges.forEach((product) => {
          const data = { product: product.node }

          tmpSwatches.push(formatProduct(data))
        })

        const activeProduct = tmpSwatches.find((product) => {
          return this.product.id === product.id
        })

        const index = tmpSwatches.indexOf(activeProduct)

        tmpSwatches.splice(index, 1)
        tmpSwatches.unshift(this.product)

        this.matchedProducts = tmpSwatches

      } catch (error) {
        reportError('Failed to load product', error)
      }
    },

    /**
     * Loads product from GraphQL.
     * @param {String} [handle] - Product handle to load.
     */
    async queryProduct(handle = this.handle) {
      if (handle === false || this.product.handle === handle) {
        return
      }

      this.state = 'loading'

      try {
        const response = await cnvs.Query({
          query: productQuery,
          variables: {
            country: this.$variable('store.country'),
            handle,
            language: this.$variable('store.language'),
          },
        })

        if (this.$variable('settings.disableComponentReadyState')) {
          return
        }

        const formattedProduct = formatProduct(response)

        this.$emit('update-request-object', {
          response: formattedProduct,
          type: 'product',
        })

        this.state = 'ready'

      } catch (error) {
        cnvs.ReportError('Failed to load product', error)
      }
    },

    /**
     * Set array of color options objects, one for each matched product.
     * @returns {Array|Boolean}
     */
    setColorOptions() {
      if (!this.product?.colorOptions || !this.product?.giftCard) {
        this.colorOptions = []
      }

      if (!this.matchedProducts?.length) {
        this.colorOptions = [
          {
            ...this.product.colorOptions[0],
            productIndex: 0,
            variants: this.product.variants,
          },
        ]

        return
      }

      const matchedColorOptions = []

      this.matchedProducts.forEach((matchedProduct, matchedProductIndex) => {
        if (!matchedProduct?.colorOptions.length) {
          return
        }

        matchedColorOptions.push({
          ...matchedProduct.colorOptions[0],
          productIndex: matchedProductIndex,
          variants: matchedProduct.variants,
        })
      })

      this.colorOptions = matchedColorOptions.filter(options => options !== null)
    },

    /**
     * Update product URL when loading new handle.
     * - Replaces state so it doesn't affect back navigation.
     * - Use pushState instead to affect back navigation.
     * - Maintains /collections/ URL.
     * @param {String} handle - New product handle.
     */
    updateProductUrl(handle) {
      const oldHandle = location.pathname.split('/').reverse()[0]

      if (oldHandle === handle) {
        return
      }

      const pathname = location.pathname.replace(oldHandle, handle)
      const url = `${location.origin}${pathname}`

      history.replaceState({
        html: '',
      }, '', url)
    },

    /**
     * Update current carousel index.
     * @param {Number} newIndex - New carousel index.
     */
    handleUpdateCurrentIndex(newIndex) {
      if (newIndex === this.currentIndex) {
        return
      }

      this.currentIndex = newIndex
    },

    /**
     * Update current carousel namespace.
     * @param {String} newNamespace - New carousel namespace.
     */
    handleUpdateCurrentNamespace(newNamespace) {
      this.currentNamespace = newNamespace
    },

    /**
     * Handle variant update from product form.
     * @param {Object} variant - Selected variant.
     * @param {Boolean} emitToCnvs - If cnvs event bus emitter should run.
     */
    handleVariantUpdate(variant, emitToCnvs = true) {
      this.$emit('update-request-object', {
        response: variant,
        type: 'variant',
      })

      /**
       * For separate product-dependant sections.
       */
      if (emitToCnvs) {
        cnvs.EventBus.emit('update-variant', variant)
      }

      const params = new URLSearchParams(document.location.search.substring(1))

      if (variant?.id) {
        params.set('variant', variant.id)
      }

      const url = variant?.id
        ? `${location.origin}${location.pathname}?${params.toString()}`
        : `${location.origin}${location.pathname}`

      history.replaceState({
        html: '',
      }, '', url)
    },

    /**
     * Update active code data variable based on current variant.
     * @param {String} colorData - Selected color data.
     */
    handleActiveSwatchUpdate(colorData) {
      this.activeSwatch = colorData
    },

    /**
     * Set intersection observer for content above the fold.
     * - Product title, prices, tagline and form.
     */
    setAboveFoldIntersectionObserver() {
      if (this.isMobile || !this.$refs.aboveFold) {
        return
      }

      this.$nextTick().then(() => {
        const element = this.$refs.aboveFold

        this.observer = setIntersectionObserver({
          callback: this.handleComponentIntersection,
          element,
          rootMargin: '0px',
          threshold: 0.1,
        })
      })
    },

    /**
     * Set intersection observer for the original Add to Cart button.
     */
    setAddToCartButtonObserver() {
      if (!this.isMobile) {
        return
      }
      this.$nextTick().then(() => {
        const originalButton = document.querySelector('.main-product__above-fold .product-form__add-to-cart')
        const footer = document.querySelector('footer, [data-component-handle="footer"]')
        const siteHeaderGroup = document.querySelector('.site-header-group')
        if (!originalButton) {
          return
        }
        const siteHeaderGroupHeight = siteHeaderGroup ? siteHeaderGroup.offsetHeight : 0
        let isFooterVisible = false
        const buttonObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.isOriginalButtonVisible = entry.isIntersecting
              if (!entry.isIntersecting && !isFooterVisible) {
                this.showStickyForm = true
              } else {
                this.showStickyForm = false
              }
            })
          },
          {
            root: null,
            rootMargin: `-${siteHeaderGroupHeight}px`,
            threshold: 0.1,
          },
        )
        buttonObserver.observe(originalButton)
        if (footer) {
          const footerObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                isFooterVisible = entry.isIntersecting
                if (isFooterVisible) {
                  this.showStickyForm = false
                } else if (!this.isOriginalButtonVisible) {
                  this.showStickyForm = true
                }
              })
            },
            {
              root: null,
              rootMargin: '0px',
              threshold: 0.1,
            },
          )
          footerObserver.observe(footer)
        }
      })
    },

    /**
     * Handle add to cart for one-size products from sticky button.
     */
    handleAddToCart() {
      // Trigger the main product form submission
      const productForm = document.querySelector('.main-product__above-fold form')
      if (productForm) {
        const submitEvent = new Event('submit', { bubbles: true, cancelable: true })
        productForm.dispatchEvent(submitEvent)
      }
    },

    /**
     * Get variant from swatch.
     * @returns {Object}
     */
    getVariantFromSwatch() {
      const activeCode = this.activeSwatch.code
      const productData = this.product || this.liquid

      return productData.variants
        .find((variant) => isColorCodeInStr(activeCode, variant.swatchUrl))
    },

    /**
     * Handle component intersection.
     * @param {Array} entries - Intersection entries.
     */
    handleComponentIntersection(entries) {
      entries.forEach((entry) => {
        this.showStickyForm = !entry.isIntersecting
      })
    },

    /**
     * Handle open product form drawer event.
     */
    handleOpenFormDrawer() {
      this.activeDrawers.productFormDrawer = !this.activeDrawers.productFormDrawer

      this.openOverlay({
        component: 'product-form-drawer',
        ignoreDismissed: true,
        namespace: 'productFormDrawer',
      })
    },

    /**
     * Handle open notify form drawer.
     * @param {Boolean} payload - Emit payload.
     */
    handleOpenNotifyMeDrawer(payload) {
      this.handleActiveDrawerUpdate('notifyMeDrawer', payload)

      this.openOverlay({
        component: 'notify-me-drawer',
        ignoreDismissed: true,
        namespace: 'notifyMeDrawer',
      })
    },

    /**
     * Set drawer active state when that overlay is closed.
     * @param {String} namespace - namespace that corresponds with key in
     * `this.activeDrawers`
     * @param {Boolean} payload - Event payload.
     */
    handleActiveDrawerUpdate(namespace, payload) {
      this.activeDrawers[namespace] = payload
    },

    /**
     * Set product form drawer active state when component's overlay is closed.
     * @param {Boolean} payload - Data from product form drawer component.
     */
    handleProductFormDrawerExpanded(payload) {
      this.activeDrawers.productFormDrawer = payload
    },

    /**
     * Events when the media array updates and the first image changes.
     * @param {String} image - New image object.
     */
    handleUpdateFeaturedImage(image) {
      this.filteredMediaImage = image
    },

    /**
     * Update confirmation title.
     * @param {Boolean} payload - Event payload.
     */
    handleConfirmationTitleUpdate(payload) {
      this.useAddMoreText = true

      if (!payload) {
        this.productFormDrawerTitle = this.$string('product.form.select_size')
        return
      }

      this.productFormDrawerTitle = this.$string('product.form.confirmation.title')
    },

    /**
     * Open size guide drawer.
     * @param {Boolean} payload - Event payload.
     */
    handleOpenSizeGuide(payload) {
      this.handleActiveDrawerUpdate('sizeGuideDrawer', payload)

      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'sizeGuideDrawer',
      })
    },

    /**
     * Open store availability drawer.
     */
    openStoreAvailabilityDrawer() {
      this.handleActiveDrawerUpdate('storeAvailabilityDrawer', true)

      this.openOverlay({
        component: 'store-availability-drawer',
        ignoreDismissed: true,
        namespace: 'storeAvailabilityDrawer',
      })
    },

    /**
     * Save the viewed product to the user's session storage.
     */
    saveToSessionStorage() {
      const storedProductsKey = 'recently-viewed-products'
      let storedProducts = sessionStorage.getItem(storedProductsKey)
        ? JSON.parse(sessionStorage.getItem(storedProductsKey))
        : []

      const targetHandle = this.product.handle || this.liquid.handle

      /**
       * Prevent duplicate product handles.
       */
      storedProducts = storedProducts
        .filter((productHandle) => productHandle !== targetHandle)
        .concat(targetHandle)

      sessionStorage.setItem(storedProductsKey, JSON.stringify(storedProducts))
    },

    /**
     * Update accordion data to include Our Commitments content, and/or
     * variant SKU as appropriate.
     * @returns {Array}
     */
    setAccordionData() {
      const hasShipping = this.section.ourShipping.content.body &&
        this.section.ourShipping.content.title &&
        this.section.ourShipping.isEnabled

      const hasTab2 = this.section.tab2.content.body &&
        this.section.tab2.content.title &&
        this.section.tab2.isEnabled

      const hasTab3 = this.section.tab3.content.body &&
        this.section.tab3.content.title &&
        this.section.tab3.isEnabled

      const hasTab4 = this.section.tab4.content.body &&
        this.section.tab4.content.title &&
        this.section.tab4.isEnabled

      const hasTab5 = this.section.tab5.content.body &&
        this.section.tab5.content.title &&
        this.section.tab5.isEnabled

      const hasCommitments = this.section.ourCommitments.content.body &&
        this.section.ourCommitments.content.title &&
        this.section.ourCommitments.isEnabled

      let accordion = this.product.accordion || this.liquid.accordion

      if (hasShipping) {
        const isDuplicate = accordion.some(panel => {
          return panel.title === this.section.ourShipping.content.title
        })
        if (!isDuplicate) {
          accordion.push(this.section.ourShipping.content)
        }
      }
      if (hasTab2) {
        const isDuplicate = accordion.some(panel => {
          return panel.title === this.section.tab2.content.title
        })
        if (!isDuplicate) {
          accordion.push(this.section.tab2.content)
        }
      }
      if (hasTab3) {
        const isDuplicate = accordion.some(panel => {
          return panel.title === this.section.tab3.content.title
        })
        if (!isDuplicate) {
          accordion.push(this.section.tab3.content)
        }
      }
      if (hasTab4) {
        const isDuplicate = accordion.some(panel => {
          return panel.title === this.section.tab4.content.title
        })
        if (!isDuplicate) {
          accordion.push(this.section.tab4.content)
        }
      }
      if (hasTab5) {
        const isDuplicate = accordion.some(panel => {
          return panel.title === this.section.tab5.content.title
        })
        if (!isDuplicate) {
          accordion.push(this.section.tab5.content)
        }
      }

      if (hasCommitments) {
        const isDuplicate = accordion.some(panel => {
          return panel.title === this.section.ourCommitments.content.title
        })

        if (!isDuplicate) {
          accordion.push(this.section.ourCommitments.content)
        }
      }

      const product = this.product || this.liquid

      if (this.variant || product.selectedOrFirstAvailableVariant) {
        const variant = this.variant || product.selectedOrFirstAvailableVariant
        const sku = variant.sku.split('_')[0]

        const descriptionPanelIndex = accordion.findIndex(panel => panel?.type === 'description')

        if (descriptionPanelIndex === -1) {
          return
        }

        if (accordion[descriptionPanelIndex].body.includes(sku)) {
          return
        }

        let descriptionPanel = accordion[descriptionPanelIndex]

        descriptionPanel = {
          ...descriptionPanel,
          body: descriptionPanel.body.concat(
            `
              <p class="main-product__sku">
                <span class="visually-hidden">
                  ${this.$string('accessibility.sku')}
                </span>
                ${sku}
              </p>
            `,
          ),
        }

        // Using splice() for Vue reactivity
        accordion.splice(descriptionPanelIndex, 1, descriptionPanel)
      }

      accordion = accordion.filter((entry) => entry.body)

      this.accordionData = accordion
    },

    /**
     * Set the active variant from the swatch.
     * - Used to format product prices when a size variant has not been set.
     */
    setVariantFromSwatch() {
      this.variantFromSwatch = this.getVariantFromSwatch()

    },

    initIntelliSuggest(profile) {
      if (this.product && this.product.variants && this.product.variants.length > 0) {

        const script = document.createElement('script')
        script.type = 'searchspring/personalized-recommendations'
        script.setAttribute('profile', profile)
        script.async = true

        const firstVariantSku = this.product.variants[0].sku

        script.innerHTML = `
        seed = "${firstVariantSku}"`

        const container = this.$refs.searchSpringContainer
        if (container) {
          container.appendChild(script)
        }
      }
    },

    setIntelliSuggest() {
      if (!window.IntelliSuggest || !this.product) {
        return
      }

      const productCode = this.product.variants[0].sku
      window.IntelliSuggest.init({ siteId: this.$variable('settings.ss_site_id'), context: `Product/${productCode}`, seed: productCode })

      this.setIntelliSuggestUser()

      window.IntelliSuggest.viewItem({ sku: productCode })
    },

    setIntelliSuggestUser() {
      if (window.cnvs.customer.id) {
        window.IntelliSuggest.setShopperId(window.cnvs.customer.id)
      }
    },
  },
}
</script>

<style lang="scss">
@import '~async/product-form-drawer/product-form-drawer';
@import '~async/product-gallery-modal/product-gallery-modal-overlay';
@import './main-product';
</style>
